import React from 'react';
import { Link, useLocation , useNavigate } from 'react-router-dom';
import { CalendarClock , Settings } from 'lucide-react';
import clsx from 'clsx';
import { Action, RouteMark } from '../../components/aa';
import type { AssignedClient } from '../../contexts/ActiveClientContext';
import { DownloadOrbit } from './DownloadOrbit';

/*
 * The client a partner is working inside, and the five places they can go in
 * it. Every client-scoped page opens with this, so the answer to "whose
 * projects am I looking at" is on the page itself and not only in the
 * switcher, and moving between Overview, Projects, Results, Schedule and
 * Client context never means leaving the client.
 */

const TABS = [
  { path: '/partner/client', label: 'Overview' },
  /* The engagement itself, as a board. It sits second because a complex
     project is the thing a partner is actually running here, and everything
     after it is a part of that project rather than a peer of it. */
  // { path: '/partner/engagement', label: 'Engagement' },
  { path: '/partner/projects', label: 'Projects' },
  { path: '/partner/results', label: 'Results' },
  { path: '/partner/schedule', label: 'Schedule' },
  // { path: '/partner/context', label: 'Client context' },
];

export const ClientHeader: React.FC<{
  client: AssignedClient;
  /** Extra controls for this page, beside the shared ones. */
  actions?: React.ReactNode;
  /** Projects has its own place in the sidebar, so it shows the client without the tab strip. */
  tabs?: boolean;
  /** Schedule call and Download Orbit. Results and Client context carry only their own action. */
  shared?: boolean;
}> = ({ client, actions, tabs = true, shared = true }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isOn = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  // The client's settings open over this page and close back to it.
  const openSettings = () => navigate('/partner/client/settings', { state: { from: `${pathname}${search}` } });

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex min-w-0 items-center gap-[18px]">
          <RouteMark glyph={client.initials} size="xl" className="bg-ink text-white" />
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{client.name}</h1>
              <button
                type="button"
                onClick={openSettings}
                aria-label={`${client.name} settings`}
                title="Client settings"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-line bg-white text-ink-muted transition-colors hover:bg-canvas-inset hover:text-ink"
              >
                <Settings size={15} />
              </button>
            </div>
            <p className="m-0 mt-1 text-[13px] text-ink-muted">{client.meta}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {shared && <Action variant="quiet" icon={<CalendarClock size={14} />} to="/partner/schedule">Schedule call</Action>}
          {shared && <DownloadOrbit clientId={client.id} clientName={client.name} />}
          {actions}
        </div>
      </div>
      {tabs && (
      <nav aria-label="Client pages" className="mt-5 flex flex-wrap gap-0.5 border-b border-line pb-2">
        {TABS.map((tab) => (
          <Link
            key={tab.path}
            to={tab.path}
            aria-current={isOn(tab.path) ? 'page' : undefined}
            className={clsx(
              'rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors',
              isOn(tab.path) && 'bg-canvas-inset text-ink',
              !isOn(tab.path) && 'text-ink-muted hover:bg-canvas-inset hover:text-ink',
            )}
          >
            {tab.label}
          </Link>
        ))}
      </nav>
      )}
    </div>
  );
};
